import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import classnames from 'classnames';
import { Link, withRouter } from 'react-router-dom';

import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';

import { shareDetailCompose } from 'mastodon/actions/compose';
import PictureInPicturePlaceholder from 'mastodon/components/picture_in_picture_placeholder';
import PollContainer from 'mastodon/containers/poll_container';
import { identityContextPropShape, withIdentity } from 'mastodon/identity_context';
import { autoPlayGif, languages as preloadedLanguages } from 'mastodon/initial_state';

import Card from '../features/status/components/card';
import Bundle from '../features/ui/components/bundle';
import { MediaGallery, Video, Audio } from '../features/ui/util/async-components';
import { displayMedia } from '../initial_state';

import { Avatar } from './avatar';
import { DisplayName } from './display_name';
import { RelativeTimestamp } from './relative_timestamp';
import { VisibilityIcon } from './visibility_icon';

const MAX_HEIGHT = 706; // 22px * 32 (+ 2px padding at the top)
const LAYOUT_BREAKPOINT = 630;

const messages = defineMessages({
  edited: { id: 'status.edited', defaultMessage: 'Edited {date}' },
});

/**
 *
 * @param {any} status
 * @returns {string}
 */


export function getStatusContent(status) {
  const ownContent = status.getIn(['translation', 'contentHtml']) || status.get('contentHtml');

  if (ownContent && ownContent.trim().length > 0) {
    return ownContent;
  }

  const reblog = status.get('reblog');
  const actualStatus = (reblog && typeof reblog === 'object') ? reblog : status;

  if (actualStatus.getIn(['translation', 'contentHtml'])) {
    return actualStatus.getIn(['translation', 'contentHtml']);
  }

  return actualStatus.get('contentHtml');
}

class TranslateButton extends PureComponent {

  static propTypes = {
    translation: ImmutablePropTypes.map,
    onClick: PropTypes.func,
  };

  render () {
    const { translation, onClick } = this.props;

    if (translation) {
      const language     = preloadedLanguages.find(lang => lang[0] === translation.get('detected_source_language'));
      const languageName = language ? language[1] : translation.get('detected_source_language');
      const provider     = translation.get('provider');

      return (
        <div className='translate-button'>
          <div className='translate-button__meta'>
            <FormattedMessage id='status.translated_from_with' defaultMessage='Translated from {lang} using {provider}' values={{ lang: languageName, provider }} />
          </div>

          <button className='link-button' onClick={onClick}>
            <FormattedMessage id='status.show_original' defaultMessage='Show original' />
          </button>
        </div>
      );
    }

    return (
      <button className='status__content__translate-button' onClick={onClick}>
        <FormattedMessage id='status.translate' defaultMessage='Translate' />
      </button>
    );
  }

}

export const defaultMediaVisibility = (status) => {
  if (!status) {
    return undefined;
  }

  if (status.get('reblog', null) !== null && typeof status.get('reblog') === 'object') {
    status = status.get('reblog');
  }

  return (displayMedia !== 'hide_all' && !status.get('sensitive') || displayMedia === 'show_all');
};

const mapStateToProps = state => ({
  languages: state.getIn(['server', 'translationLanguages', 'items']),
});

export const isMobile = (width) => width <= LAYOUT_BREAKPOINT;

class StatusContent extends PureComponent {
  static propTypes = {
    contextType: PropTypes.string,
    identity: identityContextPropShape,
    status: ImmutablePropTypes.map.isRequired,
    statusContent: PropTypes.string,
    onTranslate: PropTypes.func,
    onClick: PropTypes.func,
    collapsible: PropTypes.bool,
    onCollapsedToggle: PropTypes.func,
    languages: ImmutablePropTypes.map,
    intl: PropTypes.object,
    onOpenMedia: PropTypes.func,
    // from react-router
    match: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    isComment: PropTypes.bool,
    cacheMediaWidth: PropTypes.func,
    cachedMediaWidth: PropTypes.number,
    deployPictureInPicture: PropTypes.func,
    pictureInPicture: ImmutablePropTypes.contains({
      inUse: PropTypes.bool,
      available: PropTypes.bool,
    }),
    dispatch: PropTypes.func.isRequired,
    isImageModal: PropTypes.bool
  };

  updateOnProps = [ 'pictureInPicture' ];

  state = {
    showMedia: defaultMediaVisibility(this.props.status) && !(this.context?.hideMediaByDefault),
    showDespiteFilter: undefined,
    isExpanded: false,
  };

  _properStatus() {
    const { status } = this.props;
    if (status.get('reblog', null) !== null && typeof status.get('reblog') === 'object') {
      return status.get('reblog');
    } else {
      return status;
    }
  }

  _updateStatusLinks () {
    const node = this.node;

    if (!node) {
      return;
    }

    const { status, onCollapsedToggle } = this.props;
    const links = node.querySelectorAll('a');

    let link, mention;

    for (var i = 0; i < links.length; ++i) {
      link = links[i];

      if (link.classList.contains('status-link')) {
        continue;
      }

      link.classList.add('status-link');

      mention = this.props.status.get('mentions').find(item => link.href === item.get('url'));

      if (mention) {
        link.addEventListener('click', this.onMentionClick.bind(this, mention), false);
        link.setAttribute('title', `@${mention.get('acct')}`);
        link.setAttribute('href', `/@${mention.get('acct')}`);
        link.setAttribute('data-hover-card-account', mention.get('id'));

        const displayName = mention.get('display_name');
        if (displayName) {
          link.textContent = displayName;
        }
      } else if (link.textContent[0] === '#' || (link.previousSibling && link.previousSibling.textContent && link.previousSibling.textContent[link.previousSibling.textContent.length - 1] === '#')) {
        link.addEventListener('click', this.onHashtagClick.bind(this, link.text), false);
        link.setAttribute('href', `/tags/${link.text.replace(/^#/, '')}`);
      } else {
        link.setAttribute('title', link.href);
        link.classList.add('unhandled-link');
      }
    }

    if (status.get('collapsed', null) === null && onCollapsedToggle) {
      const { collapsible, onClick } = this.props;

      const collapsed =
          collapsible
          && onClick
          && node.clientHeight > MAX_HEIGHT
          && status.get('spoiler_text').length === 0;

      onCollapsedToggle(collapsed);
    }
  }

  handleToggleMediaVisibility = () => {
    this.setState({ showMedia: !this.state.showMedia });
  };

  handleDeployPictureInPicture = (type, mediaProps) => {
    const { deployPictureInPicture } = this.props;
    deployPictureInPicture(this._properStatus(), type, mediaProps);
  };

  handleMouseEnter = ({ currentTarget }) => {
    if (autoPlayGif) {
      return;
    }

    const emojis = currentTarget.querySelectorAll('.custom-emoji');

    for (var i = 0; i < emojis.length; i++) {
      let emoji = emojis[i];
      emoji.src = emoji.getAttribute('data-original');
    }
  };

  handleMouseLeave = ({ currentTarget }) => {
    if (autoPlayGif) {
      return;
    }

    const emojis = currentTarget.querySelectorAll('.custom-emoji');

    for (var i = 0; i < emojis.length; i++) {
      let emoji = emojis[i];
      emoji.src = emoji.getAttribute('data-static');
    }
  };

  componentDidMount () {
    this._updateStatusLinks();
  }

  componentDidUpdate () {
    this._updateStatusLinks();
  }

  onMentionClick = (mention, e) => {
    if (this.props.history && e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.props.history.push(`/@${mention.get('acct')}`);
    }
  };

  onHashtagClick = (hashtag, e) => {
    hashtag = hashtag.replace(/^#/, '');

    if (this.props.history && e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      this.props.history.push(`/tags/${hashtag}`);
    }
  };

  handleOpenMedia = (media, index) => {
    const lang = this._properStatus().getIn(['translation', 'language']) || status.get('language');
    this.props.onOpenMedia(this._properStatus().get('id'), media, index, lang);
  };

  handleMouseDown = (e) => {
    this.startXY = [e.clientX, e.clientY];
  };

  shouldIgnoreClick = (element) => {
    if (!element) {
      return false;
    }
  
    while (element) {
      if (
        (element.localName && (element.localName === 'button' || element.localName === 'a' || element.localName === 'label')) ||
        (element.classList && element.classList.contains('status__content__show-more-button'))
      ) {
        return true;
      }
      element = element.parentNode;
    }
    return false;
  };

  handleMouseUp = (e) => {
    if (!this.startXY) {
      return;
    }

    const [ startX, startY ] = this.startXY;
    const [ deltaX, deltaY ] = [Math.abs(e.clientX - startX), Math.abs(e.clientY - startY)];

    if (this.shouldIgnoreClick(e.target)) {
      this.startXY = null;
      return;
    }

    if (deltaX + deltaY < 5 && (e.button === 0 || e.button === 1) && e.detail >= 1 && this.props.onClick) {
      this.props.onClick(e);
    }

    this.startXY = null;
  };

  handleTranslate = () => {
    this.props.onTranslate();
  };

  setRef = (c) => {
    this.node = c;
  };

  handleClick = (e) => {
    const { status, history, dispatch } = this.props;
    if (!status || e.button !== 0 || e.ctrlKey || e.metaKey) return;

    e.preventDefault();
    dispatch(shareDetailCompose(status));
    const path = `/@${status.get('reblog')?.getIn(['account', 'acct'])}/${status.get('reblog')?.get('id')}`;
    history.push(path);
  };

  handleReadMoreClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isExpanded: !this.state.isExpanded });
  };

  getPlainText(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  truncateHtml(html, maxLength) {
    const plainText = this.getPlainText(html);
    if (plainText.length <= maxLength) {
      return html;
    }

    let resultHtml = '';
    let currentTextLength = 0;
    let inTag = false;
    let currentTag = '';
    let tagBuffer = '';
    let textBuffer = '';

    for (let i = 0; i < html.length; i++) {
      const char = html[i];

      if (char === '<') {
        inTag = true;
        tagBuffer += char;
        if (textBuffer) {
          const remainingLength = maxLength - currentTextLength;
          if (remainingLength > 0) {
            const textToAdd = textBuffer.slice(0, remainingLength);
            resultHtml += textToAdd;
            currentTextLength += textToAdd.length;
          }
          textBuffer = '';
        }
        continue;
      }

      if (char === '>') {
        inTag = false;
        tagBuffer += char;
        resultHtml += tagBuffer;
        currentTag = tagBuffer;
        tagBuffer = '';
        continue;
      }

      if (inTag) {
        tagBuffer += char;
      } else {
        textBuffer += char;
        if (!/^\s*$/.test(char)) {
          currentTextLength++;
        }
      }

      if (currentTextLength >= maxLength) {
        resultHtml += textBuffer + '...';
        if (currentTag.includes('<p')) {
          resultHtml += '</p>';
        }
        break;
      }
    }

    return resultHtml;
  }

  render () {
    const { status, intl, statusContent, pictureInPicture } = this.props;
    const { isExpanded } = this.state;

    const renderReadMore = this.props.onClick && status.get('collapsed');
    const contentLocale = intl?.locale.replace(/[_-].*/, '');
    const targetLanguages = this.props.languages?.get(status.get('language') || 'und');
    const renderTranslate = this.props.onTranslate && this.props.identity?.signedIn && ['public', 'unlisted'].includes(status.get('visibility')) && status.get('search_index').trim().length > 0 && targetLanguages?.includes(contentLocale);

    const transformMentions = (htmlContent, contextType) => {
      if (contextType !== 'notifications') {
        return htmlContent;
      }

      return htmlContent.replace(
        /<a\s+([^>]*\s+)?class="[^"]*mention[^"]*"[^>]*>(.*?)<\/a>/g,
        (match, attributes, innerContent) => {
          return `<span ${attributes || ''}>${innerContent}</span>`;
        }
      );
    };

    const content = { __html: statusContent ?? getStatusContent(status) };
    const transformedContent = {
      __html: transformMentions(content.__html, this.props.contextType),
    };
    const language = status.getIn(['translation', 'language']) || status.get('language');
    const classNames = classnames('status__content', {
      'status__content--with-action': this.props.onClick && this.props.history,
      'status__content--collapsed': renderReadMore,
      "status__content--expanded": isExpanded,
    });

    let media;
    const matchedFilters = status.get('matched_filters');
    const expanded = (!matchedFilters || this.state.showDespiteFilter) && (!status.get('hidden') || status.get('spoiler_text').length === 0);

    let displayContent = transformedContent;
    let showReadMoreLink = false;
    
    const isStatusDetail = /^\/@[^\/]+\/\d+$/.test(location.pathname);

    const containsOnlyLinks = (html) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const body = doc.body;
    
      const childNodes = Array.from(body.childNodes).filter(
        (node) => node.nodeType !== Node.TEXT_NODE || node.textContent.trim() !== ''
      );
    
      return (
        childNodes.length > 0 &&
        childNodes.every((node) => node.nodeName.toLowerCase() === 'a')
      );
    };

    if (!isStatusDetail || this.props.isImageModal) {
      const plainText = this.getPlainText(transformedContent.__html);
      if (plainText.length > 150) {
        if (!isExpanded) {
          displayContent = { __html: this.truncateHtml(transformedContent.__html, 150) };
        }
        showReadMoreLink = true;
      }
    } else if (containsOnlyLinks(transformedContent.__html)) {
      showReadMoreLink = false;
    }

    // const { statusContentProps, hashtagBar } = getHashtagBarForStatus(this._properStatus());

    const readMoreButton = renderReadMore && (
      <div className='status__content__read-more-button' onClick={this.props.onClick} key='read-more'>
        <FormattedMessage id='status.show_more' defaultMessage='Show more' />
      </div>
    );

    const translateButton = renderTranslate && (
      <TranslateButton onClick={this.handleTranslate} translation={status.get('translation')} />
    );

    const poll = !!status.get('poll') && (
      <PollContainer pollId={status.get('poll')} status={status} lang={language} />
    );

    if (pictureInPicture && pictureInPicture.get('inUse')) {
      media = <PictureInPicturePlaceholder aspectRatio={this.getAttachmentAspectRatio()} />;
    } else if (this._properStatus().get('media_attachments').size > 0) {
      const language = status.getIn(['translation', 'language']) || status.get('language');
      if (['image', 'gifv', 'unknown'].includes(this._properStatus().getIn(['media_attachments', 0, 'type'])) || this._properStatus().get('media_attachments').size > 1) {
        media = (
          <Bundle fetchComponent={MediaGallery} loading={this.renderLoadingMediaGallery}>
            {Component => (
              <Component
                media={this._properStatus().get('media_attachments')}
                lang={language}
                sensitive={this._properStatus().get('sensitive')}
                height={110}
                onOpenMedia={this.handleOpenMedia}
                cacheWidth={this.props.cacheMediaWidth}
                defaultWidth={this.props.cachedMediaWidth}
                visible={this.state.showMedia}
                onToggleVisibility={this.handleToggleMediaVisibility}
              />
            )}
          </Bundle>
        );
      } else if (this._properStatus().getIn(['media_attachments', 0, 'type']) === 'audio') {
        const attachment = this._properStatus().getIn(['media_attachments', 0]);
        const description = attachment.getIn(['translation', 'description']) || attachment.get('description');
        media = (
          <Bundle fetchComponent={Audio} loading={this.renderLoadingAudioPlayer}>
            {Component => (
              <Component
                src={attachment.get('url')}
                alt={description}
                lang={language}
                poster={attachment.get('preview_url') || this._properStatus().getIn(['account', 'avatar_static'])}
                backgroundColor={attachment.getIn(['meta', 'colors', 'background'])}
                foregroundColor={attachment.getIn(['meta', 'colors', 'foreground'])}
                accentColor={attachment.getIn(['meta', 'colors', 'accent'])}
                duration={attachment.getIn(['meta', 'original', 'duration'], 0)}
                width={this.props.cachedMediaWidth}
                height={110}
                cacheWidth={this.props.cacheMediaWidth}
                deployPictureInPicture={pictureInPicture && pictureInPicture.get('available') ? this.handleDeployPictureInPicture : undefined}
                sensitive={this._properStatus().get('sensitive')}
                blurhash={attachment.get('blurhash')}
                visible={this.state.showMedia}
                onToggleVisibility={this.handleToggleMediaVisibility}
              />
            )}
          </Bundle>
        );
      } else if (this._properStatus().getIn(['media_attachments', 0, 'type']) === 'video') {
        const attachment = this._properStatus().getIn(['media_attachments', 0]);
        const description = attachment.getIn(['translation', 'description']) || attachment.get('description');
        media = (
          <Bundle fetchComponent={Video} loading={this.renderLoadingVideoPlayer}>
            {Component => (
              <Component
                preview={attachment.get('preview_url')}
                frameRate={attachment.getIn(['meta', 'original', 'frame_rate'])}
                aspectRatio={`${attachment.getIn(['meta', 'original', 'width'])} / ${attachment.getIn(['meta', 'original', 'height'])}`}
                blurhash={attachment.get('blurhash')}
                src={attachment.get('url')}
                alt={description}
                lang={language}
                sensitive={this._properStatus().get('sensitive')}
                onOpenVideo={this.handleOpenVideo}
                deployPictureInPicture={pictureInPicture && pictureInPicture.get('available') ? this.handleDeployPictureInPicture : undefined}
                visible={this.state.showMedia}
                onToggleVisibility={this.handleToggleMediaVisibility}
              />
            )}
          </Bundle>
        );
      }
    } else if (this._properStatus().get('card')) {
      media = (
        <Card
          onOpenMedia={this.handleOpenMedia}
          card={this._properStatus().get('card')}
          compact
          sensitive={this._properStatus().get('sensitive')}
        />
      );
    }

    if(status.get('reblog')) {
      return (
        <div
          className={classNames}
          ref={this.setRef}
          tabIndex={0}
          onMouseDown={this.handleMouseDown}
          onMouseUp={this.handleMouseUp}
          key='status-content'
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
        >
          <div className='status__content__text status__content__text--visible translate' lang={language} dangerouslySetInnerHTML={displayContent} />
          {showReadMoreLink && (
            <div className='status__content__show-more-button' onClick={this.handleReadMoreClick}>
              {isExpanded ? <FormattedMessage
                id='status.show_less'
                defaultMessage='Show less'
              /> : <FormattedMessage
                id='status.show_more'
                defaultMessage='Show more'
              />
              }
            </div>
          )}
          <div
            className={classnames('status__content-wrapper', {
              'status__content-wrapper--reblog': true,
            })}
            onClick={this.handleClick}
          >
            <div onMouseUp={this.handleMouseUp} className='status__info'>
              <div className='status__detail-button-wrapper' />
              <Link
                to={`/@${status.get('reblog').getIn(['account', 'acct'])}`}
                title={status.get('reblog').getIn(['account', 'acct'])}
                data-hover-card-account={status.get('reblog').getIn(['account', 'id'])}
                className='status__display-name'
              >
                <div className='status__avatar'>
                  <Avatar account={(status.get('reblog') || status).get('account')} size={36} />
                </div>
                <div className='status_account_and_time_container'>
                  <DisplayName account={status.get('reblog').get('account')} />
                  <div className='status__relative-time'>
                    <RelativeTimestamp timestamp={status.get('reblog').get('created_at')} />
                    <span className='dot-separator-tiny'>•</span>
                    <span className='status__visibility-icon'>
                      <VisibilityIcon visibility={status.get('reblog').get('visibility')} />
                    </span>
                    {status.get('reblog').get('edited_at') && (
                      <abbr
                        title={intl.formatMessage(messages.edited, {
                          date: intl.formatDate(status.get('edited_at'), {
                            year: 'numeric',
                            month: 'short',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          }),
                        })}
                      />
                    )}
                  </div>
                </div>
              </Link>
            </div>
            {expanded && (
              <>
                <StatusContent
                  status={this._properStatus()}
                  onTranslate={this.handleTranslate}
                  collapsible
                  onCollapsedToggle={this.handleCollapsedToggle}
                  // {...statusContentProps}
                  isComment={this.props.isComment}
                />
                <Link to={`/@${this._properStatus().getIn(['account', 'acct'])}/${this._properStatus().get('id')}`}>
                  {media}
                </Link>
                {/* {hashtagBar} */}
              </>
            )}
          </div>
        </div>
      );
    }

    if (!this.props.isComment) {
      return (
        <>
          <div className={classNames} ref={this.setRef} tabIndex={0} onMouseDown={this.handleMouseDown} onMouseUp={this.handleMouseUp} key='status-content' onMouseEnter={this.handleMouseEnter} onMouseLeave={this.handleMouseLeave}>
            <div className='status__content__text status__content__text--visible translate' lang={language} dangerouslySetInnerHTML={displayContent} />
            {showReadMoreLink && (
              <div className='status__content__show-more-button' onClick={this.handleReadMoreClick}>
                {isExpanded ? <FormattedMessage
                  id='status.show_less'
                  defaultMessage='Show less'
                /> : <FormattedMessage
                  id='status.show_more'
                  defaultMessage='Show more'
                />
                }
              </div>
            )}
            {poll}
            {translateButton}
          </div>

          {readMoreButton}
        </>
      );
    } else {
      return (
        <div className={`${classNames} status__content__text--closer`} ref={this.setRef} tabIndex={0} onMouseEnter={this.handleMouseEnter} onMouseLeave={this.handleMouseLeave}>
          <div className='status__content__text status__content__text--visible translate' lang={language} dangerouslySetInnerHTML={displayContent} />

          {poll}
          {translateButton}
        </div>
      );
    }
  }

}

export default withRouter(withIdentity(connect(mapStateToProps)(injectIntl(StatusContent))));
